import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  _delete,
  _get,
  _getBlob,
  _patch,
  _post,
  _postFile,
} from "../../Services/baseApi";
import {
  DEVICE_CREATE_URL,
  DEVICE_DELETE_URL,
  DEVICE_DETAILS_URL,
  deviceLidarCalUrl,
  DEVICE_EXPORT_URL,
  DEVICE_IMPORT_URL,
  DEVICE_LIST_URL,
  DEVICE_RAILWAY_IMPORT_URL,
  DEVICE_RAILWAY_SAMPLE_URL,
  DEVICE_SAMPLE_URL,
  DEVICE_UPDATE_URL,
} from "../../Services/endpoints";
import { errorHandler, successToast } from "../../Utils/toast";
import {
  downloadBlobFile,
  getFilenameFromDisposition,
} from "../../Utils/downloadFile";
import {
  mqttIdsEqual,
  normalizeMqttId,
  resolveRoleDisplayOnline,
} from "../../Utils/telemetryConnectivityUtils";

const INITIAL_STATE = {
  deviceList: [],
  deviceDetails: null,
  isLoading: false,
  isCmdLoading: false,
  total: 0,
};

export const createDeviceThunk = createAsyncThunk(
  "device/create",
  async (payload, { rejectWithValue }) => {
    try {
      const { handleFun, ...body } = payload;
      const response = await _post(DEVICE_CREATE_URL, body);
      successToast(response?.data?.message || "Device created");
      handleFun?.();
      return response?.data?.result;
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error);
    }
  },
);

export const getDeviceListThunk = createAsyncThunk(
  "device/list",
  async (params, { rejectWithValue }) => {
    try {
      const response = await _get(DEVICE_LIST_URL, {
        page: params.page,
        limit: params.limit,
        name: params.searchText || undefined,
        key: params.sortColumn || "createdAt",
        keyOrder: params.sortDirection || "DESC",
        stateId: params.stateId || undefined,
        cityId: params.cityId || undefined,
        zoneId: params.zoneId || undefined,
        wardId: params.wardId || undefined,
        masterStatus: params.masterStatus || undefined,
        slaveStatus: params.slaveStatus || undefined,
      });
      return response?.data?.result;
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error);
    }
  },
);

export const getDeviceDetailsThunk = createAsyncThunk(
  "device/details",
  async (id, { rejectWithValue }) => {
    try {
      const response = await _get(`${DEVICE_DETAILS_URL}/${id}`);
      return response?.data?.result;
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error);
    }
  },
);

export const updateDeviceThunk = createAsyncThunk(
  "device/update",
  async ({ id, handleFun, ...payload }, { rejectWithValue }) => {
    try {
      const response = await _patch(`${DEVICE_UPDATE_URL}/${id}`, payload);
      successToast(response?.data?.message || "Device updated");
      handleFun?.();
      return response?.data?.result;
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error);
    }
  },
);

const cmdSuccessToast = (data, mqttDeviceId, label = "Command") => {
  const count = data?.published ?? data?.deviceIds?.length ?? 0;
  const unitWord = count === 1 ? "unit" : "units";
  const mqttId = mqttDeviceId?.trim?.() || mqttDeviceId;
  const targetHint = mqttId ? ` (${mqttId})` : "";
  successToast(
    data?.message || `${label} sent to ${count} ${unitWord}${targetHint}.`,
  );
};

export const sendDeviceCmdThunk = createAsyncThunk(
  "device/cmd",
  async ({ id, ...cmdBody }, { rejectWithValue }) => {
    try {
      const response = await _post(`${DEVICE_DETAILS_URL}/${id}/cmd`, cmdBody);
      const data = response?.data;
      cmdSuccessToast(data, cmdBody.mqttDeviceId);
      return data;
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error);
    }
  },
);

export const sendDeviceLidarCalThunk = createAsyncThunk(
  "device/lidar-cal",
  async ({ id, mqttDeviceId, samples, existingLevelCm }, { rejectWithValue }) => {
    try {
      const response = await _post(deviceLidarCalUrl(id), {
        mqttDeviceId,
        samples: Number(samples),
        existingLevelCm: Number(existingLevelCm),
      });
      const data = response?.data;
      cmdSuccessToast(data, mqttDeviceId, "LiDAR calibration");
      return data;
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error);
    }
  },
);

export const deleteDeviceThunk = createAsyncThunk(
  "device/delete",
  async ({ id, handleFun }, { rejectWithValue }) => {
    try {
      const response = await _delete(`${DEVICE_DELETE_URL}/${id}`);
      successToast(response?.data?.message || "Device deleted");
      handleFun?.();
      return response?.data;
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error);
    }
  },
);

export const exportDevicesThunk = createAsyncThunk(
  "device/export",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await _getBlob(DEVICE_EXPORT_URL, payload?.params || {});
      const filename = getFilenameFromDisposition(
        response.headers["content-disposition"],
        "devices-export.xlsx",
      );
      downloadBlobFile(response.data, filename);
      successToast("Devices exported successfully");
      return true;
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error?.response?.data || error?.message);
    }
  },
);

export const downloadDeviceSampleThunk = createAsyncThunk(
  "device/sample",
  async (_, { rejectWithValue }) => {
    try {
      const response = await _getBlob(DEVICE_SAMPLE_URL);
      const filename = getFilenameFromDisposition(
        response.headers["content-disposition"],
        "devices-sample.xlsx",
      );
      downloadBlobFile(response.data, filename);
      return true;
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error?.response?.data || error?.message);
    }
  },
);

export const importDevicesThunk = createAsyncThunk(
  "device/import",
  async ({ file }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await _postFile(DEVICE_IMPORT_URL, formData);
      return response?.data;
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error?.response?.data || error?.message);
    }
  },
);

export const downloadRailwayDeviceSampleThunk = createAsyncThunk(
  "device/railway-sample",
  async (_, { rejectWithValue }) => {
    try {
      const response = await _getBlob(DEVICE_RAILWAY_SAMPLE_URL);
      const filename = getFilenameFromDisposition(
        response.headers["content-disposition"],
        "railway-devices-sample.xlsx",
      );
      downloadBlobFile(response.data, filename);
      return true;
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error?.response?.data || error?.message);
    }
  },
);

export const importRailwayDevicesThunk = createAsyncThunk(
  "device/railway-import",
  async ({ file }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await _postFile(DEVICE_RAILWAY_IMPORT_URL, formData);
      return response?.data;
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error?.response?.data || error?.message);
    }
  },
);

const mergeLiveEquipmentFields = (current, incoming) => {
  const next = { ...current };
  for (const [key, value] of Object.entries(incoming)) {
    if (!Object.prototype.hasOwnProperty.call(incoming, key)) continue;
    if (key === "online") {
      const coerced = resolveRoleDisplayOnline(value);
      if (coerced === true || coerced === false) next[key] = coerced;
      continue;
    }
    if (typeof value === "boolean" || typeof value === "number") {
      next[key] = value;
    } else if (value !== undefined && value !== null && value !== "") {
      next[key] = value;
    }
  }
  return next;
};

const sameMqttId = (left, right) => mqttIdsEqual(left, right);

const patchEquipmentByMqttId = (state, mqttDeviceId, patch) => {
  if (!state.deviceDetails || !mqttDeviceId) return;

  if (sameMqttId(state.deviceDetails.masterDeviceId, mqttDeviceId)) {
    state.deviceDetails.master = {
      ...(state.deviceDetails.master ?? {}),
      ...patch,
    };
    return;
  }

  if (sameMqttId(state.deviceDetails.slaveDeviceId, mqttDeviceId)) {
    state.deviceDetails.slave = {
      ...(state.deviceDetails.slave ?? {}),
      ...patch,
    };
  }
};

const deviceSlice = createSlice({
  name: "device",
  initialState: INITIAL_STATE,
  reducers: {
    clearDeviceDetails: (state) => {
      state.deviceDetails = null;
    },
    updateDeviceEquipmentLive: (state, action) => {
      const payload = action.payload ?? {};
      const { deviceId, deviceRole, updatedAt, ...equipmentFields } = payload;
      if (!deviceId) return;

      const nextUpdatedAt = updatedAt ?? new Date().toISOString();
      const online = resolveRoleDisplayOnline(equipmentFields.online) ?? undefined;
      const statusUpdatedAt =
        equipmentFields.statusUpdatedAt ??
        (online === true || online === false ? nextUpdatedAt : undefined);

      state.deviceList = state.deviceList.map((row) => {
        if (sameMqttId(row?.masterDeviceId, deviceId)) {
          return {
            ...row,
            masterOnline: online ?? row.masterOnline,
            masterUpdatedAt: nextUpdatedAt,
            ...(statusUpdatedAt
              ? { masterStatusUpdatedAt: statusUpdatedAt }
              : {}),
            masterSlavePowerStatus:
              equipmentFields.status ?? row.masterSlavePowerStatus,
            masterSafetyTimeoutMs:
              equipmentFields.safetyTimeoutMs ?? row.masterSafetyTimeoutMs,
          };
        }
        if (sameMqttId(row?.slaveDeviceId, deviceId)) {
          return {
            ...row,
            slaveOnline: online ?? row.slaveOnline,
            slaveUpdatedAt: nextUpdatedAt,
            ...(statusUpdatedAt
              ? { slaveStatusUpdatedAt: statusUpdatedAt }
              : {}),
          };
        }
        return row;
      });

      if (!state.deviceDetails) return;

      if (sameMqttId(state.deviceDetails.masterDeviceId, deviceId)) {
        state.deviceDetails = {
          ...state.deviceDetails,
          masterOnline: online ?? state.deviceDetails.masterOnline,
          ...(statusUpdatedAt
            ? { masterStatusUpdatedAt: statusUpdatedAt }
            : {}),
          master: mergeLiveEquipmentFields(
            {
              ...(state.deviceDetails.master ?? {}),
              deviceId,
              deviceRole:
                deviceRole ??
                state.deviceDetails.master?.deviceRole ??
                "master",
              updatedAt: nextUpdatedAt,
              ...(statusUpdatedAt ? { statusUpdatedAt } : {}),
            },
            equipmentFields,
          ),
        };
        return;
      }

      if (sameMqttId(state.deviceDetails.slaveDeviceId, deviceId)) {
        state.deviceDetails = {
          ...state.deviceDetails,
          slaveOnline: online ?? state.deviceDetails.slaveOnline,
          ...(statusUpdatedAt
            ? { slaveStatusUpdatedAt: statusUpdatedAt }
            : {}),
          slave: mergeLiveEquipmentFields(
            {
              ...(state.deviceDetails.slave ?? {}),
              deviceId,
              deviceRole:
                deviceRole ?? state.deviceDetails.slave?.deviceRole ?? "slave",
              updatedAt: nextUpdatedAt,
              ...(statusUpdatedAt ? { statusUpdatedAt } : {}),
            },
            equipmentFields,
          ),
        };
      }
    },
  },
  extraReducers: (builder) => {
    const setLoading = (state, value) => {
      state.isLoading = value;
    };

    builder
      .addCase(createDeviceThunk.pending, (state) => setLoading(state, true))
      .addCase(createDeviceThunk.fulfilled, (state) => setLoading(state, false))
      .addCase(createDeviceThunk.rejected, (state) => setLoading(state, false));

    builder
      .addCase(getDeviceListThunk.pending, (state) => setLoading(state, true))
      .addCase(getDeviceListThunk.fulfilled, (state, action) => {
        const incoming = action.payload?.findDevices || [];
        const prevByMqtt = new Map();
        for (const row of state.deviceList ?? []) {
          if (row?.masterDeviceId) {
            prevByMqtt.set(normalizeMqttId(row.masterDeviceId), {
              masterOnline: row.masterOnline,
              masterStatusUpdatedAt: row.masterStatusUpdatedAt,
            });
          }
          if (row?.slaveDeviceId) {
            prevByMqtt.set(normalizeMqttId(row.slaveDeviceId), {
              slaveOnline: row.slaveOnline,
              slaveStatusUpdatedAt: row.slaveStatusUpdatedAt,
            });
          }
        }
        state.deviceList = incoming.map((row) => {
          const masterPrev = prevByMqtt.get(normalizeMqttId(row?.masterDeviceId));
          const slavePrev = prevByMqtt.get(normalizeMqttId(row?.slaveDeviceId));
          return {
            ...row,
            masterOnline:
              masterPrev?.masterOnline === true ||
              masterPrev?.masterOnline === false
                ? masterPrev.masterOnline
                : row.masterOnline,
            slaveOnline:
              slavePrev?.slaveOnline === true || slavePrev?.slaveOnline === false
                ? slavePrev.slaveOnline
                : row.slaveOnline,
            masterStatusUpdatedAt:
              masterPrev?.masterStatusUpdatedAt || row.masterStatusUpdatedAt,
            slaveStatusUpdatedAt:
              slavePrev?.slaveStatusUpdatedAt || row.slaveStatusUpdatedAt,
          };
        });
        state.total = action.payload?.totalCount || 0;
        state.isLoading = false;
      })
      .addCase(getDeviceListThunk.rejected, (state) =>
        setLoading(state, false),
      );

    builder
      .addCase(getDeviceDetailsThunk.pending, (state) =>
        setLoading(state, true),
      )
      .addCase(getDeviceDetailsThunk.fulfilled, (state, action) => {
        state.deviceDetails = action.payload;
        state.isLoading = false;
      })
      .addCase(getDeviceDetailsThunk.rejected, (state) =>
        setLoading(state, false),
      );

    builder
      .addCase(updateDeviceThunk.pending, (state) => setLoading(state, true))
      .addCase(updateDeviceThunk.fulfilled, (state, action) => {
        state.deviceDetails = action.payload;
        state.isLoading = false;
      })
      .addCase(updateDeviceThunk.rejected, (state) => setLoading(state, false));

    builder
      .addCase(deleteDeviceThunk.pending, (state) => setLoading(state, true))
      .addCase(deleteDeviceThunk.fulfilled, (state) => setLoading(state, false))
      .addCase(deleteDeviceThunk.rejected, (state) => setLoading(state, false));

    const setCmdLoading = (state, value) => {
      state.isCmdLoading = value;
    };

    builder
      .addCase(sendDeviceCmdThunk.pending, (state) => setCmdLoading(state, true))
      .addCase(sendDeviceCmdThunk.fulfilled, (state) =>
        setCmdLoading(state, false),
      )
      .addCase(sendDeviceCmdThunk.rejected, (state) =>
        setCmdLoading(state, false),
      );

    builder
      .addCase(sendDeviceLidarCalThunk.pending, (state) =>
        setCmdLoading(state, true),
      )
      .addCase(sendDeviceLidarCalThunk.fulfilled, (state, action) => {
        setCmdLoading(state, false);
        const { mqttDeviceId, samples, existingLevelCm } = action.meta.arg;
        patchEquipmentByMqttId(state, mqttDeviceId, {
          lidarCalSamples: samples,
          lidarRefCm: existingLevelCm,
        });
      })
      .addCase(sendDeviceLidarCalThunk.rejected, (state) =>
        setCmdLoading(state, false),
      );
  },
});

export const { clearDeviceDetails, updateDeviceEquipmentLive } =
  deviceSlice.actions;
export default deviceSlice.reducer;
